import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ComponentBaseComponent } from '@app/core/components/component-base/component-base.component';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { Item } from '@app/core/components/listbox/listbox.component';
import { DataTaskService } from '@app/core/services/data-task.service';
import { FileItem, FileService } from '@app/core/services/file.service';
import { ModalService } from '@app/core/services/modal.service';
import { DocArcService } from '@app/services/doc-arc.service';
import { MergeDocument, PdfService } from '@app/services/pdf.service';
import { DocumentsMergedPreviewComponent } from './documents-merged-preview/documents-merged-preview.component';
import { StateService } from '@app/core/services/state.service';
import { DocumentsGridComponent } from '@app/shared/documents-grid/documents-grid.component';
import { DocumentsGet, DocumentsParams, GetDocumentsParams } from '@app/services/document.service';
import { StateDialogContext, TT_STATE_DIALOG } from '@app/modal/state-modal/state-modal.component';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';
import { Message, NullableOption, Recipient } from '@microsoft/microsoft-graph-types';
import { EmailCreateService } from '@app/services/email-create.service';

@GridModalComponent('documents')
@Component({
    selector: 'tt-documents',
    templateUrl: './documents.component.html',
    styleUrls: ['./documents.component.css'],
})
export class DocumentsComponent extends ComponentBaseComponent implements OnInit, OnChanges {
    @Input()
    public params?: DocumentsParams;

    @Output()
    public getLoaded = new EventEmitter<DocumentsGet>();

    @ViewChild(DocumentsGridComponent)
    public documentsGrid?: DocumentsGridComponent;

    public get?: DocumentsGet;

    public filesToUpload: (File | Message)[] = [];

    public selectedDocumentType: string = 'd';

    public uploading = false;

    public grid: GridOptions = new Grid()
        .setRememberId('w_documents.documents_grid')
        .setLoadDataFunction({ method: 2972, parameters: () => ({ ...this.params, doctype: this.selectedDocumentType, onlyFilenames: '0', searchtext: '' }) })
        .setCustomToolbarButtons([
            {
                name: 'merged_preview',
                text: 'merge_files_and_preview',
                translate: true,
                cssClass: 'tt-button tt-button--primary im-grid-btn-xs-l',
                disable_if_no_selected: true,
                func: async () => {
                    const docfilenames = this.grid.gridfunc!.getIsSelectedRows().map((row) => row['docfilename']);

                    const progress = this.modal.openProgressDialog({ value: 0, max: docfilenames.length, indeterminate: true, rememberId: 'w_documents.progress.merge' });

                    progress.updateIndeterminate(true);

                    let count = 0;

                    const fileArrayBuffers = await Promise.all(
                        docfilenames.map(async (label) => {
                            try {
                                const arraybuffer = await this.file.download(this.base64UrlEncodeUnicode(label), { suppressErrorMessage: true });
                                progress.updateValue((count = count + 1));
                                let binary = '';
                                let bytes = new Uint8Array(arraybuffer);
                                let len = bytes.byteLength;
                                for (let i = 0; i < len; i++) {
                                    binary += String.fromCharCode(bytes[i]);
                                }

                                return arraybuffer;
                            } catch (error) {
                                progress.updateStatus('invalid');
                                progress.updateInvalidText(label);
                                console.error(error);
                            }

                            return null;
                        }),
                    );
                    try {
                        // @ts-ignore
                        const mergeDocuments: MergeDocument[] = fileArrayBuffers.filter((arrayBuffer) => !!arrayBuffer).map((arrayBuffer) => ({ content: arrayBuffer, contentType: 'application/pdf' }));

                        const pdf = await this.pdf.mergePDFs(mergeDocuments, { includeTableOfContents: false, fileName: this.getFileName() });
                        this.dialog.open(DocumentsMergedPreviewComponent, { data: { src: this.getUrl(pdf, 'documents_merged_pdf.pdf'), fileName: this.getFileName() } });
                        progress.updateIndeterminate(false);
                        progress.dialogRef.close();
                    } catch (error) {
                        console.error(error);
                    }
                },
            },
        ]);

    private getFileName(): string {
        return `${new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substring(0, 16).replaceAll('T', '_').replaceAll(':', '').replaceAll('-', '')}_${this.state.getCurrentName()}_${Object.entries(this.state.getStateParams())
            .filter((val) => val[0] !== '#')
            .reduce((acc, val) => acc + `${val[1]}_`, '')}merged_documents.pdf`;
    }

    private getUrl(base64Data: string, filename: string) {
        const base64 = base64Data.replace(/^data:application\/pdf;base64,/, '');

        const byteCharacters = atob(base64);
        const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);

        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const file = new File([blob], filename, { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(file);

        return blobUrl;
    }

    public documents: Item[] = [];

    constructor(
        private datatask: DataTaskService,
        private docarc: DocArcService,
        private file: FileService,
        private pdf: PdfService,
        private modal: ModalService,
        private dialog: MatDialog,
        private state: StateService,
        private emailCreate: EmailCreateService,
        @Optional() @Inject(TT_STATE_DIALOG) stateDialog: StateDialogContext,
    ) {
        super();

        this.initFromDialog(stateDialog);
    }

    public async uploadFiles() {
        if (this.filesToUpload.length === 0 || !this.params || !this.get) return;
        const files: File[] = this.filesToUpload.filter((item): item is File => item instanceof File);
        const messages: Message[] = this.filesToUpload.filter((item): item is Message => !(item instanceof File));

        this.uploading = true;

        try {
            await this.file.uploadFiles({
                files,
                preprocess: async (fileName, file) => {
                    if (!this.params) return;

                    const checksum = await this.file.hash(file);
                    const { exists } = await this.docarc.checkFileExists(fileName, checksum.hash, this.params.argtype, this.params.argvalue);

                    return { cancelUpload: exists, checksum_md5: checksum.hash };
                },
                postprocess: (data) => this.addFileToDocarc(data),
            });

            for (let message of messages) {
                await this.docarc.saveEmailItem({
                    heading: this.base64UrlEncodeUnicode(message.subject ?? ''),
                    email_body_html: this.base64UrlEncodeUnicode(message.body?.content ?? ''),
                    recipient_email: this.getSeperatedListOfAddresses(message.toRecipients),
                    datetime_sent: message.sentDateTime ?? '',
                    datetime_received: message.receivedDateTime ?? '',
                    sentDateTimeZulu: message.sentDateTime ?? '',
                    sender_email: message.sender?.emailAddress?.address ?? '',
                    sender_name: message.sender?.emailAddress?.name ?? '',
                    email_cc: this.getSeperatedListOfAddresses(message.ccRecipients),
                    email_bcc: this.getSeperatedListOfAddresses(message.bccRecipients),
                    attachments: message.attachments ?? [],
                    argtype: this.get.argtype,
                    argvalue: this.get.argvalue,
                });
            }

            this.filesToUpload = [];
        } catch (error) {
            console.error(error);
        } finally {
            this.uploading = false;
            await this.read();
        }
    }

    private getSeperatedListOfAddresses(recipients?: NullableOption<Recipient[]>, seperator: string = ','): string {
        return (recipients ?? [])
            .map((recipient) => recipient.emailAddress?.address)
            .filter((address) => !!address)
            .join(seperator);
    }

    private addFileToDocarc(data: FileItem & { checksum_md5: string; checksum: string }) {
        if (!this.params) return;

        const docarc = {
            contentType: data.contentType || '',
            filename: data.fileName || '',
            localFileName: data.localFileName || '', // BJS 20211019
            // originalFileName: data.originalFileName || '',
            originalTimestamp: data.originalDateTime || '',
            argType: this.params.argtype || '',
            argValue: this.params.argvalue || '',
            size: data.size || 0,
            checksum: data.checksum || '',
            latitude: data.latitude || 0,
            longitude: data.longitude || 0,
            altitude: data.altitude || 0,
            heading: data.heading || 0,
            checksum_md5: data.checksum_md5 || 0,
        };

        if (!!docarc.filename?.trim()) {
            return this.docarc.add(docarc);
        } else {
            throw Error('Filename is required');
        }
    }

    public async read() {
        await this.loadDocuments();
        this.documentsGrid?.grid?.gridfunc?.read();
        this.grid.gridfunc?.read();
    }

    public async openSettings(event: MouseEvent) {
        event.preventDefault();

        await this.modal.openStateDialog({ state: 'webpagesettingmenu', params: 'documents' }, { width: '90vw', height: '80vh' });
        await this.loadGet();
        await this.read();
    }

    private async loadGet() {
        this.get = (await this.datatask.Post(895, this.params))[0];
        this.getLoaded.emit(this.get);
    }

    private async loadDocuments() {
        if (!!this.params && !!this.selectedDocumentType) {
            this.documents = await this.datatask.Post(309, { ...this.params, max_nbr_of_rows: 5000, searchtext: '', doctype: this.selectedDocumentType, onlyFilenames: '0', glyphicon_select: '1' });
        }
    }

    private async init() {
        await this.loadGet();
        await this.read();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }
    ngOnInit(): void {
        // this.init();
    }

    private async initFromDialog(state: StateDialogContext) {
        if (!!state && 'argtype' in state.params && 'argvalue' in state.params) {
            state.setSize({ height: '100vh', width: '100vw' });
            this.params = state.params as unknown as DocumentsParams;
            await this.init();
            state.setHeading(this.get?.page_heading ?? '');
        }
    }
}
